import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, MessageSquare, MoreVertical, RotateCcw, Users, Download, AlertCircle, Clock, ChevronDown, ChevronUp, X, Check, Key, Shield } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../../services/userService';
import { showToast } from '../../components/Toast';
import { getFullMediaUrl } from '../../utils/urlHelper';

const MemberDetailPage: React.FC = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [editFormData, setEditFormData] = useState<any>(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null);
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);

  const { data: member, isLoading } = useQuery({
    queryKey: ['memberDetail', memberId],
    queryFn: () => memberId ? userService.getMemberDetails(memberId) : Promise.reject('No member ID'),
  });

  const { data: activePrograms = [] } = useQuery({
    queryKey: ['memberActivePrograms', memberId],
    queryFn: () => memberId ? userService.getMemberActivePrograms(memberId) : Promise.resolve([]),
    enabled: !!memberId,
  });

  const updateMemberMutation = useMutation({
    mutationFn: async (data: FormData) => {
      return memberId ? userService.updateMember(memberId, data) : Promise.reject('No member ID');
    },
    onSuccess: () => {
      setIsEditModalOpen(false);
      // Invalidate and refetch the member detail query
      queryClient.invalidateQueries({ queryKey: ['memberDetail', memberId] });
      queryClient.refetchQueries({ queryKey: ['memberDetail', memberId] });
    },
    onError: (err: any) => {
      showToast(err.message || 'Failed to update member', 'error');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (data: { newPassword: string }) => {
      return memberId ? userService.resetMemberPassword(memberId, data.newPassword) : Promise.reject('No member ID');
    },
    onSuccess: () => {
      showToast('Password reset successfully! Please share the new password with the member.', 'success');
      setIsResetPasswordModalOpen(false);
      setResetPasswordData({ newPassword: '', confirmPassword: '' });
    },
    onError: (err: any) => {
      showToast(err.message || 'Failed to reset password', 'error');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Loading member details...</div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Member not found</div>
      </div>
    );
  }

  // Handle both flat and nested data structures from backend
  const memberData = member.data || member;
  const profile = memberData.profile || memberData;
  const healthData = memberData.healthData || {};
  const membership = memberData.membership || {};

  const firstName = profile.firstName || 'Member';
  const lastName = profile.lastName || '';
  const firstNameChar = firstName.charAt(0).toUpperCase();
  const lastNameChar = lastName.charAt(0).toUpperCase();
  
  // Get profile photo URL - check both possible field names
  const profilePhotoUrl = memberData.profilePhotoUrl || memberData.profile?.photo || profile.photo || profile.profilePhotoUrl;

  const handleEditClick = () => {
    setEditFormData({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      email: profile.email || '',
      phone: profile.phone || '',
      dob: profile.dob ? new Date(profile.dob).toISOString().split('T')[0] : '',
      gender: profile.gender || '',
      height: healthData.height || '',
      weight: healthData.weight || '',
      activityLevel: healthData.activityLevel || 'moderately_active',
      medicalConditions: healthData.medicalConditions || '',
    });
    setEditPhotoPreview(profilePhotoUrl);
    setEditPhotoFile(null);
    setIsEditModalOpen(true);
  };

  const handleEditPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result as string);
        setEditPhotoFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('firstName', editFormData.firstName);
    formData.append('lastName', editFormData.lastName);
    formData.append('email', editFormData.email);
    formData.append('phone', editFormData.phone);
    formData.append('dob', editFormData.dob);
    formData.append('gender', editFormData.gender);
    formData.append('height', editFormData.height);
    formData.append('weight', editFormData.weight);
    formData.append('activityLevel', editFormData.activityLevel);
    formData.append('medicalConditions', editFormData.medicalConditions);

    if (editPhotoFile) {
      formData.append('profilePhoto', editPhotoFile);
    }

    updateMemberMutation.mutate(formData);
  };

  const handleResetPasswordClick = () => {
    setResetPasswordData({ newPassword: '', confirmPassword: '' });
    setIsResetPasswordModalOpen(true);
  };

  const handleResetPasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setResetPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResetPasswordSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    if (resetPasswordData.newPassword.length < 6) {
      showToast('Password must be at least 6 characters long', 'error');
      return;
    }

    resetPasswordMutation.mutate({ newPassword: resetPasswordData.newPassword });
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, label: '', color: '' };
    if (password.length < 6) return { strength: 1, label: 'Weak', color: 'text-red-500' };
    if (password.length < 8) return { strength: 2, label: 'Fair', color: 'text-yellow-500' };
    if (password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password)) {
      return { strength: 4, label: 'Strong', color: 'text-green-500' };
    }
    return { strength: 3, label: 'Good', color: 'text-blue-500' };
  };

  return (
    <div className="min-h-screen bg-gray-950 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/members')}
          className="flex items-center gap-2 text-gray-400 hover:text-[#23F47C] transition-all mb-8"
        >
          <ArrowLeft size={20} />
          Back to Members
        </button>

        {/* Header Section */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-6 flex-1">
              {/* Profile Picture */}
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0 border-2 border-gray-700">
                  {profilePhotoUrl ? (
                    <img
                      src={profilePhotoUrl}
                      alt={firstName}
                      className="w-32 h-32 rounded-full object-cover"
                      onError={(e) => {
                        console.error('❌ Failed to load profile photo:', profilePhotoUrl);
                        console.error('❌ Image element:', e.target);
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log('✅ Profile photo loaded successfully:', profilePhotoUrl);
                      }}
                    />
                  ) : (
                    <span className="text-gray-400 text-4xl font-bold">
                      {firstNameChar}{lastNameChar}
                    </span>
                  )}
                </div>
                <div className="absolute bottom-0 right-0 w-8 h-8 bg-[#23F47C] rounded-full border-3 border-gray-900"></div>
              </div>

              {/* Member Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h1 className="text-4xl font-bold text-white">{firstName} {lastName}</h1>
                  <span className="px-4 py-2 bg-[#23F47C] text-gray-900 text-xs font-bold rounded-full">
                    {membership.status || 'Active'}
                  </span>
                </div>
                <p className="text-gray-400 mb-4 text-lg">Member ID: #GN-{memberData._id?.slice(-4).toUpperCase() || 'N/A'}</p>
                <div className="flex items-center gap-6 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <Clock size={18} className="text-[#23F47C]" />
                    Joined {membership.startedAt ? new Date(membership.startedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }) : 'N/A'}
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertCircle size={18} className="text-[#23F47C]" />
                    Identity Verified
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button 
                onClick={handleEditClick}
                title="Edit Profile"
                className="p-3 text-gray-400 hover:text-[#23F47C] transition-all"
              >
                <Edit2 size={20} />
              </button>
              <button 
                title="Message"
                className="p-3 text-gray-400 hover:text-[#23F47C] transition-all"
              >
                <MessageSquare size={20} />
              </button>
              <button className="p-3 text-gray-400 hover:text-[#23F47C] transition-all">
                <MoreVertical size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-3 gap-8">
          {/* Left Column - Personal & Health Info */}
          <div className="col-span-2 space-y-8">
            {/* Personal & Health Information */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <div className="w-1 h-8 bg-[#23F47C] rounded"></div>
                Personal & Health Information
              </h2>

              <div className="grid grid-cols-3 gap-8 mb-8">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Email Address</p>
                  <p className="text-white font-medium text-lg">{profile.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Phone Number</p>
                  <p className="text-white font-medium text-lg">{profile.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Date of Birth</p>
                  <p className="text-white font-medium text-lg">
                    {profile.dob ? new Date(profile.dob).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-8">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Current Weight</p>
                  <p className="text-white font-medium text-lg">{healthData.weight || 'N/A'} lbs</p>
                  {healthData.weight && <p className="text-xs text-[#23F47C] mt-2">-2 lbs this month</p>}
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Height</p>
                  <p className="text-white font-medium text-lg">{healthData.height ? `${healthData.height} cm` : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 font-semibold">Activity Level</p>
                  <p className="text-white font-medium text-lg">
                    {healthData.activityLevel ? healthData.activityLevel.replace(/_/g, ' ').charAt(0).toUpperCase() + healthData.activityLevel.slice(1).replace(/_/g, ' ') : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Medical Conditions */}
            {(healthData.medicalConditions && healthData.medicalConditions.length > 0) && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                  <AlertCircle size={24} className="text-[#23F47C]" />
                  Medical Conditions & Notes
                </h3>
                <p className="text-gray-300 text-base leading-relaxed">
                  {healthData.medicalConditions.join(', ')}
                </p>
              </div>
            )}

            {/* Active Program */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-3">
                  <Users size={24} className="text-[#23F47C]" />
                  Active Programs
                </h3>
                {activePrograms && activePrograms.length > 2 && (
                  <button
                    onClick={() => setShowAllPrograms(!showAllPrograms)}
                    className="flex items-center gap-2 text-[#23F47C] text-sm font-medium hover:opacity-80 transition-opacity"
                  >
                    {showAllPrograms ? (
                      <>
                        <span>Show Less</span>
                        <ChevronUp size={18} />
                      </>
                    ) : (
                      <>
                        <span>View All ({activePrograms.length})</span>
                        <ChevronDown size={18} />
                      </>
                    )}
                  </button>
                )}
              </div>
              {activePrograms && activePrograms.length > 0 ? (
                <div className="space-y-6">
                  {(showAllPrograms ? activePrograms : activePrograms.slice(0, 2)).map((enrollment: any) => {
                    const program = enrollment.program;
                    const totalDays = program?.durationWeeks ? program.durationWeeks * 7 : 30;
                    const progress = enrollment.currentDay ? (enrollment.currentDay / totalDays) * 100 : 0;
                    
                    return (
                      <div key={enrollment._id} className="flex gap-6">
                        {program?.imageUrl ? (
                          <img
                            src={getFullMediaUrl(program.imageUrl) || ''}
                            alt={program.title}
                            className="w-32 h-32 bg-gray-700 rounded-lg object-cover flex-shrink-0"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-32 h-32 bg-gray-700 rounded-lg flex-shrink-0 flex items-center justify-center">
                            <Users size={40} className="text-gray-600" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h4 className="text-[#23F47C] text-2xl font-bold mb-2">{program?.title || 'Program'}</h4>
                          <p className="text-gray-400 text-base mb-4">
                            {enrollment.assignedInstructor 
                              ? `Instructor: ${enrollment.assignedInstructor.firstName} ${enrollment.assignedInstructor.lastName}`
                              : 'Self-paced program'
                            }
                          </p>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between text-base">
                              <span className="text-gray-400">Progress: {Math.round(progress)}%</span>
                              <span className="text-gray-400">DAY {enrollment.currentDay} / {totalDays}</span>
                            </div>
                            <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                              <div className="h-full bg-[#23F47C]" style={{ width: `${progress}%` }}></div>
                            </div>
                            <p className="text-gray-400 text-base">
                              Status: <span className="text-[#23F47C] font-semibold">{enrollment.status}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-gray-400 text-center py-8">
                  <p>No active programs assigned</p>
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-3">
                  <Clock size={24} className="text-[#23F47C]" />
                  Recent Activity
                </h3>
                <button className="text-[#23F47C] text-base font-medium hover:underline">View All</button>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all cursor-pointer">
                  <div className="w-12 h-12 bg-[#23F47C] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Users size={20} className="text-gray-900" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium text-base">Upper Body Power - Session Completed</p>
                    <p className="text-gray-400 text-sm">Today, 08:30 AM • 1h 15m • 420 kcal</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all cursor-pointer">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <Download size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium text-base">Body Metrics Updated</p>
                    <p className="text-gray-400 text-sm">Yesterday, 07:15 PM • Weight: 185 lbs (Down 0.5 lbs)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Quick Actions & Support */}
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-bold text-white mb-6">Quick Actions</h3>
              <div className="space-y-4">
                <button 
                  onClick={handleResetPasswordClick}
                  className="w-full flex flex-col items-center gap-3 p-6 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
                >
                  <RotateCcw size={28} className="text-[#23F47C]" />
                  <span className="text-base font-medium text-white">Reset PWD</span>
                </button>
                <button className="w-full flex flex-col items-center gap-3 p-6 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all">
                  <AlertCircle size={28} className="text-[#23F47C]" />
                  <span className="text-base font-medium text-white">Suspend</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Edit Member Profile</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="p-2 text-gray-400 hover:text-white transition-all"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
              {/* Photo Upload */}
              <div>
                <label className="block text-gray-400 text-sm mb-4 font-semibold">Profile Photo</label>
                <div className="flex items-center gap-6">
                  <div className="w-24 h-24 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0 border-2 border-gray-700 overflow-hidden">
                    {editPhotoPreview ? (
                      <img src={editPhotoPreview} alt="Preview" className="w-24 h-24 object-cover" />
                    ) : (
                      <span className="text-gray-400 text-2xl font-bold">{firstNameChar}{lastNameChar}</span>
                    )}
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditPhotoUpload}
                      className="hidden"
                    />
                    <span className="inline-block bg-[#23F47C] text-gray-900 px-4 py-2 rounded-lg font-semibold hover:bg-opacity-90 transition-all">
                      Upload Photo
                    </span>
                  </label>
                </div>
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={editFormData?.firstName || ''}
                    onChange={handleEditInputChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={editFormData?.lastName || ''}
                    onChange={handleEditInputChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={editFormData?.email || ''}
                    onChange={handleEditInputChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={editFormData?.phone || ''}
                    onChange={handleEditInputChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    value={editFormData?.dob || ''}
                    onChange={handleEditInputChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Gender</label>
                  <select
                    name="gender"
                    value={editFormData?.gender || ''}
                    onChange={handleEditInputChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Health Data */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Height (cm)</label>
                  <input
                    type="number"
                    name="height"
                    value={editFormData?.height || ''}
                    onChange={handleEditInputChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Weight (kg)</label>
                  <input
                    type="number"
                    name="weight"
                    value={editFormData?.weight || ''}
                    onChange={handleEditInputChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Activity Level</label>
                  <select
                    name="activityLevel"
                    value={editFormData?.activityLevel || ''}
                    onChange={handleEditInputChange}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                  >
                    <option value="sedentary">Sedentary</option>
                    <option value="lightly_active">Lightly Active</option>
                    <option value="moderately_active">Moderately Active</option>
                    <option value="very_active">Very Active</option>
                    <option value="extremely_active">Extremely Active</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Medical Conditions</label>
                <textarea
                  name="medicalConditions"
                  value={editFormData?.medicalConditions || ''}
                  onChange={handleEditInputChange}
                  rows={3}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-800">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-6 py-2 text-gray-400 hover:text-white transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMemberMutation.isPending}
                  className="px-6 py-2 bg-[#23F47C] text-gray-900 rounded-lg font-semibold hover:bg-opacity-90 transition-all disabled:opacity-50"
                >
                  {updateMemberMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {isResetPasswordModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#23F47C] rounded-lg flex items-center justify-center">
                  <Key size={20} className="text-gray-900" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Reset Password</h2>
                  <p className="text-gray-400 text-sm">Set new password for {firstName} {lastName}</p>
                </div>
              </div>
              <button
                onClick={() => setIsResetPasswordModalOpen(false)}
                className="p-2 text-gray-400 hover:text-white transition-all"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleResetPasswordSubmit} className="p-6 space-y-6">
              {/* Warning Notice */}
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Shield size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-yellow-500 font-semibold text-sm">Security Notice</p>
                    <p className="text-gray-300 text-sm mt-1">
                      You will need to securely share this password with the member. 
                      Consider using a secure communication method.
                    </p>
                  </div>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-gray-400 text-sm mb-2 font-semibold">New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={resetPasswordData.newPassword}
                  onChange={handleResetPasswordInputChange}
                  placeholder="Enter new password"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#23F47C] transition-all"
                  required
                />
                {resetPasswordData.newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Password Strength:</span>
                      <span className={getPasswordStrength(resetPasswordData.newPassword).color}>
                        {getPasswordStrength(resetPasswordData.newPassword).label}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-700 rounded-full mt-1 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 transition-all duration-300"
                        style={{ width: `${(getPasswordStrength(resetPasswordData.newPassword).strength / 4) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-gray-400 text-sm mb-2 font-semibold">Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={resetPasswordData.confirmPassword}
                  onChange={handleResetPasswordInputChange}
                  placeholder="Confirm new password"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#23F47C] transition-all"
                  required
                />
                {resetPasswordData.confirmPassword && resetPasswordData.newPassword !== resetPasswordData.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
                )}
                {resetPasswordData.confirmPassword && resetPasswordData.newPassword === resetPasswordData.confirmPassword && (
                  <p className="text-green-500 text-sm mt-1 flex items-center gap-1">
                    <Check size={16} />
                    Passwords match
                  </p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-800 rounded-lg p-4">
                <p className="text-gray-400 text-sm font-semibold mb-2">Password Requirements:</p>
                <ul className="text-gray-400 text-sm space-y-1">
                  <li className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${resetPasswordData.newPassword.length >= 6 ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                    At least 6 characters
                  </li>
                  <li className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${resetPasswordData.newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                    8+ characters recommended
                  </li>
                  <li className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${/[A-Z]/.test(resetPasswordData.newPassword) ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                    Include uppercase letter
                  </li>
                  <li className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${/[0-9]/.test(resetPasswordData.newPassword) ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                    Include number
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-800">
                <button
                  type="button"
                  onClick={() => setIsResetPasswordModalOpen(false)}
                  className="px-6 py-2 text-gray-400 hover:text-white transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    resetPasswordMutation.isPending || 
                    !resetPasswordData.newPassword || 
                    !resetPasswordData.confirmPassword ||
                    resetPasswordData.newPassword !== resetPasswordData.confirmPassword ||
                    resetPasswordData.newPassword.length < 6
                  }
                  className="px-6 py-2 bg-[#23F47C] text-gray-900 rounded-lg font-semibold hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {resetPasswordMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Key size={16} />
                      Reset Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberDetailPage;